import * as React from 'react'
import { Pencil } from 'lucide-react'

import { QuotePaymentScheduleAmountTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { formatCurrency } from '@/lib/currency-utils'
import {
  buildQuotePaymentScheduleTemplate,
  type PaymentScheduleFormValue,
} from '@/lib/payment-schedule-utils'
import { formatQuotePaymentScheduleDue } from '@/lib/quote-payment-schedule-defaults'
import { cn } from '@/lib/utils'
import { QuotePaymentScheduleEditor } from './quote-payment-schedule-editor'

type PaymentSchedulePreset = 'deposit' | 'full' | 'custom'

const PRESETS: Array<{ id: PaymentSchedulePreset; label: string; template?: string }> = [
  { id: 'deposit', label: '50% deposit', template: '50-50' },
  { id: 'full', label: 'Paid in full', template: 'full-upfront' },
  { id: 'custom', label: 'Custom' },
]

function isPercentage(schedule: PaymentScheduleFormValue): boolean {
  return schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE
}

/** Which preset pill the current schedule rows correspond to. */
export function detectQuotePaymentSchedulePreset(
  schedules: PaymentScheduleFormValue[],
): PaymentSchedulePreset {
  if (schedules.length === 1 && isPercentage(schedules[0]) && schedules[0].percentage === 100) {
    return 'full'
  }
  if (
    schedules.length === 2
    && schedules.every((schedule) => isPercentage(schedule) && schedule.percentage === 50)
  ) {
    return 'deposit'
  }
  return 'custom'
}

export function getQuotePaymentScheduleAmountCents(
  schedule: PaymentScheduleFormValue,
  totalCents: number,
): number {
  return isPercentage(schedule)
    ? Math.round(((schedule.percentage || 0) / 100) * totalCents)
    : (schedule.amountCents || 0)
}

export interface QuoteBuilderPaymentCardProps {
  sectionId: string
  schedules: PaymentScheduleFormValue[]
  totalCents: number
  onSchedulesChange: (schedules: PaymentScheduleFormValue[]) => void
  onApplyBalanceToLastPayment: () => void
  onAddBalanceAsNewPayment: () => void
  useCustomPaymentMethods: boolean
  onUseCustomPaymentMethodsChange: (checked: boolean) => void
  /** Rendered when custom checkout methods are switched on. */
  paymentMethodsControl: React.ReactNode
  depositDueDays?: number
  remainderDueDays?: number
  eventDates?: Array<Date | undefined | null>
  disabled?: boolean
  /** Shows the due-anchor controls in the schedule editor (see QuotePaymentScheduleEditor). */
  editableDueAnchors?: boolean
  /** Enforced tip collected with the last payment; shown on that row when set. */
  lastPaymentTipCents?: number
  /** Overrides the default card heading. */
  title?: string
}

export function QuoteBuilderPaymentCard({
  sectionId,
  schedules,
  totalCents,
  onSchedulesChange,
  onApplyBalanceToLastPayment,
  onAddBalanceAsNewPayment,
  useCustomPaymentMethods,
  onUseCustomPaymentMethodsChange,
  paymentMethodsControl,
  depositDueDays,
  remainderDueDays,
  eventDates,
  disabled = false,
  editableDueAnchors = false,
  lastPaymentTipCents = 0,
  title = 'Payment Schedule',
}: QuoteBuilderPaymentCardProps) {
  const [customOpen, setCustomOpen] = React.useState(false)
  const activePreset = detectQuotePaymentSchedulePreset(schedules)
  const showEditor = customOpen || activePreset === 'custom'

  const handlePreset = (preset: (typeof PRESETS)[number]) => {
    if (!preset.template) {
      setCustomOpen(true)
      return
    }
    setCustomOpen(false)
    onSchedulesChange(buildQuotePaymentScheduleTemplate(preset.template, {
      depositDays: depositDueDays,
      remainderDays: remainderDueDays,
      eventDates,
    }))
  }

  return (
    <section
      id={sectionId}
      aria-labelledby={`${sectionId}-heading`}
      tabIndex={-1}
      className="scroll-mt-24 space-y-4 rounded-2xl border bg-card p-5 focus:outline-none"
    >
      <div className="flex flex-wrap items-center gap-3">
        <h2 id={`${sectionId}-heading`} className="text-base font-semibold">{title}</h2>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => {
            const isActive = preset.id === 'custom' ? showEditor : activePreset === preset.id
            return (
              <button
                key={preset.id}
                type="button"
                aria-pressed={isActive}
                disabled={disabled}
                onClick={() => handlePreset(preset)}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm transition-colors disabled:opacity-50',
                  isActive
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'hover:bg-muted',
                )}
              >
                {preset.label}
              </button>
            )
          })}
        </div>
      </div>

      {schedules.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2">
          {schedules.map((schedule, index) => (
            <div
              key={`${schedule.sequenceNumber}-${index}`}
              className="flex items-center justify-between gap-3 rounded-xl border px-4 py-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {schedule.description || `Payment ${index + 1}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatQuotePaymentScheduleDue(schedule)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-sm tabular-nums">
                  ${formatCurrency(getQuotePaymentScheduleAmountCents(schedule, totalCents))}
                  {lastPaymentTipCents > 0 && index === schedules.length - 1 && (
                    <span className="ml-1 text-xs text-muted-foreground">+ ${formatCurrency(lastPaymentTipCents)} tip</span>
                  )}
                </span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => setCustomOpen(true)}
                  className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50"
                >
                  <Pencil className="size-3.5" aria-hidden="true" />
                  <span className="sr-only">
                    Edit {schedule.description || `payment ${index + 1}`}
                  </span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showEditor && (
        <QuotePaymentScheduleEditor
          schedules={schedules}
          quoteTotalCents={totalCents}
          onSchedulesChange={onSchedulesChange}
          disabled={disabled}
          onApplyBalanceToLastPayment={onApplyBalanceToLastPayment}
          onAddBalanceAsNewPayment={onAddBalanceAsNewPayment}
          depositDueDays={depositDueDays}
          remainderDueDays={remainderDueDays}
          eventDates={eventDates}
          editableDueAnchors={editableDueAnchors}
        />
      )}

      <div className="space-y-3 border-t pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Switch
              id="quote-custom-payment-methods"
              checked={useCustomPaymentMethods}
              onCheckedChange={onUseCustomPaymentMethodsChange}
              disabled={disabled}
            />
            <Label htmlFor="quote-custom-payment-methods" className="text-sm font-medium">
              Customize checkout methods
            </Label>
          </div>
          {!useCustomPaymentMethods && (
            <p className="text-sm text-muted-foreground">
              Using organization defaults — Credit Card, ACH
            </p>
          )}
        </div>
        {useCustomPaymentMethods && paymentMethodsControl}
      </div>
    </section>
  )
}
